import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Button, Typography } from "@mui/material";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, createContext, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { isNetworkOrCorsError } from "@api/clients";

/** Threshold of consecutive network errors before showing the error screen */
const NETWORK_ERROR_THRESHOLD = 3;

interface NetworkHealthCtx {
  networkError: boolean;
  resetNetworkError: () => void;
}

const NetworkHealthContext = createContext<NetworkHealthCtx>({
  networkError: false,
  resetNetworkError: () => {}
});

export const useNetworkHealth = () => useContext(NetworkHealthContext);

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  const [networkError, setNetworkError] = useState(false);
  const [networkErrorCount, setNetworkErrorCount] = useState(0);

  const resetNetworkError = useCallback(() => {
    setNetworkError(false);
    setNetworkErrorCount(0);
  }, []);

  // Create queryClient inside the component so it can access setNetworkError
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            if (isNetworkOrCorsError(error)) {
              setNetworkErrorCount((prev) => {
                const next = prev + 1;
                if (next >= NETWORK_ERROR_THRESHOLD) {
                  setNetworkError(true);
                }
                return next;
              });
            }
          },
          onSuccess: () => {
            // Any successful query resets the counter
            setNetworkErrorCount(0);
          }
        }),
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000,
            retry: (failureCount, error) => {
              // Never retry CORS / network errors — they won't self-heal
              if (isNetworkOrCorsError(error)) return false;
              return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            refetchOnReconnect: true
          },
          mutations: {
            retry: 0
          }
        }
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <NetworkHealthContext.Provider
        value={{ networkError, resetNetworkError }}
      >
        {networkError ? (
          <NetworkErrorFallback onRetry={resetNetworkError} />
        ) : (
          children
        )}
      </NetworkHealthContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </QueryClientProvider>
  );
};

/** Full-screen error shown when APIs are consistently failing with CORS/network errors */
function NetworkErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("base");

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
      gap={2}
      p={4}
      textAlign="center"
      role="alert"
      aria-live="assertive"
    >
      <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main" }} />
      <Typography variant="h2Bold" color="error">
        {t("eInvoicingApp.networkError.title")}
      </Typography>
      <Typography color="text.secondary" maxWidth={480}>
        {t("eInvoicingApp.networkError.description")}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={onRetry}
        aria-label={t("eInvoicingApp.retry")}
      >
        {t("eInvoicingApp.retry")}
      </Button>
    </Box>
  );
}
